/**
 * Feedback Store (Zustand)
 *
 * Global state management for the voice feedback capture feature.
 * Persists active sessions to IndexedDB so they survive page reloads (P0 requirement).
 *
 * Pattern follows retirementStore.ts — uses `create` from zustand.
 */

import { create } from "zustand";
import type {
  FeedbackSession,
  FeedbackSessionStatus,
  FeedbackStore,
  SaveResult,
  VoiceComment,
} from "../types/feedback";
import { createLogger } from "../utils/logger";

const feedbackStoreLogger = createLogger({ prefix: "FeedbackStore" });

// ---------------------------------------------------------------------------
// IndexedDB helpers
// ---------------------------------------------------------------------------

const DB_NAME = "feedbackStore";
const DB_VERSION = 1;
const STORE_NAME = "sessions";
const SESSION_KEY = "active";

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function idbGet<T>(key: string): Promise<T | undefined> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result as T | undefined);
    request.onerror = () => reject(request.error);
  });
}

async function idbPut<T>(key: string, value: T): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const request = store.put(value, key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

async function idbDelete(key: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const request = store.delete(key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// ---------------------------------------------------------------------------
// Session name generator
// ---------------------------------------------------------------------------

function generateSessionName(): string {
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `feedback-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}-${pad(now.getHours())}h${pad(now.getMinutes())}`;
}

// ---------------------------------------------------------------------------
// localStorage helpers for lightweight UI state
// ---------------------------------------------------------------------------

const PANEL_OPEN_KEY = "feedbackPanelOpen";

function persistPanelOpen(open: boolean): void {
  try {
    localStorage.setItem(PANEL_OPEN_KEY, String(open));
  } catch {
    // localStorage unavailable — ignore silently
  }
}

function readPersistedPanelOpen(): boolean | null {
  try {
    const value = localStorage.getItem(PANEL_OPEN_KEY);
    if (value === "true") return true;
    if (value === "false") return false;
    return null;
  } catch {
    return null;
  }
}

function clearPersistedPanelOpen(): void {
  try {
    localStorage.removeItem(PANEL_OPEN_KEY);
  } catch {
    // localStorage unavailable — ignore silently
  }
}

// ---------------------------------------------------------------------------
// Store factory
// ---------------------------------------------------------------------------

function createFeedbackStoreState() {
  return (
    set: (
      partial:
        | Partial<FeedbackStore>
        | ((state: FeedbackStore) => Partial<FeedbackStore>)
    ) => void,
    get: () => FeedbackStore
  ): FeedbackStore => ({
    // State
    session: null,
    panelOpen: false,
    isRecording: false,
    interimTranscript: "",
    recordingDuration: 0,
    lastSaveResult: null,

    // Actions
    startSession: () => {
      set({
        session: {
          sessionName: generateSessionName(),
          startedAt: new Date().toISOString(),
          comments: [],
          status: "active" as const,
        },
        panelOpen: true,
        lastSaveResult: null,
      });
      persistPanelOpen(true);
      // Persist immediately
      void get().persistSession();
    },

    endSession: () => {
      set({
        session: null,
        panelOpen: false,
        isRecording: false,
        interimTranscript: "",
        recordingDuration: 0,
      });
      clearPersistedPanelOpen();
      void get().clearPersistedSession();
    },

    togglePanel: () => {
      set((state) => {
        const next = !state.panelOpen;
        persistPanelOpen(next);
        return { panelOpen: next };
      });
    },

    setPanelOpen: (open: boolean) => {
      set({ panelOpen: open });
      persistPanelOpen(open);
    },

    addComment: (comment: VoiceComment) => {
      set((state) => {
        if (!state.session) return {};
        const updated: FeedbackSession = {
          ...state.session,
          comments: [...state.session.comments, comment],
        };
        return { session: updated };
      });
      void get().persistSession();
    },

    deleteComment: (index: number) => {
      set((state) => {
        if (!state.session) return {};
        const updated: FeedbackSession = {
          ...state.session,
          comments: state.session.comments.filter((c) => c.index !== index),
        };
        return { session: updated };
      });
      void get().persistSession();
    },

    updateTranscription: (index: number, text: string) => {
      set((state) => {
        if (!state.session) return {};
        const updated: FeedbackSession = {
          ...state.session,
          comments: state.session.comments.map((c) =>
            c.index === index ? { ...c, transcription: text } : c
          ),
        };
        return { session: updated };
      });
      void get().persistSession();
    },

    updateTitle: (index: number, title: string) => {
      set((state) => {
        if (!state.session) return {};
        const updated: FeedbackSession = {
          ...state.session,
          comments: state.session.comments.map((c) =>
            c.index === index ? { ...c, title } : c
          ),
        };
        return { session: updated };
      });
      void get().persistSession();
    },

    setCommentSummary: (index: number, summary: string) => {
      set((state) => {
        if (!state.session) return {};
        const updated: FeedbackSession = {
          ...state.session,
          comments: state.session.comments.map((c) =>
            c.index === index ? { ...c, summary } : c
          ),
        };
        return { session: updated };
      });
      void get().persistSession();
    },

    setCommentParaphrase: (index: number, paraphrase: string) => {
      set((state) => {
        if (!state.session) return {};
        const updated: FeedbackSession = {
          ...state.session,
          comments: state.session.comments.map((c) =>
            c.index === index ? { ...c, paraphrase } : c
          ),
        };
        return { session: updated };
      });
      void get().persistSession();
    },

    setRecording: (recording: boolean) => {
      set({ isRecording: recording });
    },

    setInterimTranscript: (text: string) => {
      set({ interimTranscript: text });
    },

    setRecordingDuration: (seconds: number) => {
      set({ recordingDuration: seconds });
    },

    setSessionStatus: (status: FeedbackSessionStatus) => {
      set((state) => {
        if (!state.session) return {};
        return { session: { ...state.session, status } };
      });
      void get().persistSession();
    },

    setSessionName: (name: string) => {
      set((state) => {
        if (!state.session) return {};
        return { session: { ...state.session, sessionName: name } };
      });
      void get().persistSession();
    },

    setLastSaveResult: (result: SaveResult | null) => {
      set({ lastSaveResult: result });
    },

    persistSession: async () => {
      const { session } = get();
      if (!session) return;
      try {
        await idbPut(SESSION_KEY, session);
      } catch (error) {
        feedbackStoreLogger.debug("IndexedDB unavailable for session persist", {
          error,
        });
      }
    },

    restoreSession: async () => {
      try {
        const session = await idbGet<FeedbackSession>(SESSION_KEY);
        if (
          session &&
          (session.status === "active" || session.status === "error")
        ) {
          const persistedOpen = readPersistedPanelOpen();
          set({ session, panelOpen: persistedOpen ?? true });
        }
      } catch (error) {
        feedbackStoreLogger.debug("IndexedDB unavailable for session restore", {
          error,
        });
      }
    },

    clearPersistedSession: async () => {
      try {
        await idbDelete(SESSION_KEY);
      } catch (error) {
        feedbackStoreLogger.debug("IndexedDB unavailable for session clear", {
          error,
        });
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Singleton store
// ---------------------------------------------------------------------------

export const useFeedbackStore = create<FeedbackStore>()(
  createFeedbackStoreState()
);

/**
 * Export factory for testing — creates isolated store instances.
 */
export { createFeedbackStoreState };
